// 加工小贴士 API + 枚举字典. 条目内容单源存于小贴士库, 材料详情页只做挂载引用;
// 查重/分叉/复用判定在后端, 前端按 SaveTipResult 的联合形态分支处理.

import { MsgResult, PageResult, request, requestWithMsg, uploadFile } from './request';
import { I18n } from '../utils/i18n';

export type TipType = 'safety' | 'special' | 'general';

export interface Tip {
    id: number;
    /** 业务主键, 前缀按类型: X(安全提示) / S(特殊处理) / G(通用), 如 G01 */
    tipCode: string;
    type: TipType;
    /** 图标: 平台存储的图片地址 (2026-10-16 起不再收 emoji / 图标 key), 客户端"注意事项"卡片渲染用 */
    icon?: string;
    title: string;
    /** 补充说明 (怎么做 + 为什么/后果) */
    description: string;
    /** 适用光源, 空数组 = 全部光源适用 */
    light: string[];
    /** 适用加工类型, 空数组 = 全部加工类型适用 */
    craft: string[];
    /** 引用数 (材料维度) = 挂载的材料数 */
    refCount: number;
    /** 派生: 已挂载材料的一级分类聚合 (中文名), 条目本身不声明分类 */
    usedCats: string[];
    /** 引用数 (设备维度, 2026-08-27) = 挂载的设备台数 */
    deviceRefCount: number;
    /** 派生: 已挂载设备名 (按挂载顺序去重) */
    usedDevices: string[];
    createdAt?: string;
    updatedAt?: string;
}

export interface ListTipsParams {
    keyword?: string;
    type?: string;
    light?: string;
    craft?: string;
    cat1?: string;
    /** 派生筛选: 已挂载在该设备上的条目 */
    deviceKey?: string;
    /** 排除已挂载在该设备上的条目 (设备添加弹窗候选集) */
    excludeDeviceKey?: string;
    page?: number;
    pageSize?: number;
}

/** 同名不同说明的查重提示项 */
export interface TipDuplicate {
    tipCode: string;
    /** 说明前 36 字摘要 */
    descSnippet: string;
}

export interface SaveTipPayload {
    type: string;
    /** 图标: 平台存储的图片地址, 可空 */
    icon?: string;
    title: string;
    description: string;
    light: string[];
    craft: string[];
    /** 材料上下文: 新建时保存后自动挂载; 编辑时引用数>1 触发分叉 */
    xcode?: string;
    /** 设备上下文 (与 xcode 互斥, 2026-08-27): 语义同上, 只是挂载对象是设备 */
    deviceKey?: string;
    /** 同名软提示的二次确认 */
    confirmDup?: boolean;
}

/**
 * 保存条目的联合响应:
 * - {tipCode}: 正常创建 / 更新
 * - {reused:true, tipCode}: 内容完全命中已有条目, 已直接引用 (未新建副本)
 * - {needConfirm:true, duplicates}: 同名不同说明, 需二次确认 (再次保存带 confirmDup)
 * - {forked:true, newTipCode, otherRefs}: 材料上下文编辑多引用条目 → 分叉
 */
export interface SaveTipResult {
    tipCode?: string;
    reused?: boolean;
    needConfirm?: boolean;
    duplicates?: TipDuplicate[];
    forked?: boolean;
    newTipCode?: string;
    otherRefs?: number;
}

/** 引用数下钻: 条目已挂载的设备 (2026-08-27) */
export interface TipDeviceRef {
    deviceKey: string;
    name: string;
    family: string;
    archCode: string;
}

/** 引用数下钻: 条目已挂载的材料 (name 为多语言 map, 展示用 i18nText 取值) */
export interface TipMaterialRef {
    xcode: string;
    name: I18n;
    type1: string;
    type2: string;
}

const BASE = '/v1';

export function listTips(params: ListTipsParams): Promise<PageResult<Tip>> {
    return request<PageResult<Tip>>(`${BASE}/tips`, { query: params });
}

/**
 * 拉取全部有效条目 (分页循环). 库条目量小 (预期 < 150), 全量取回后在前端做
 * 筛选/计数/同名标记 —— 「显示 X / Y」「类型（N）」等派生量需要未筛选全集.
 */
export async function listAllTips(): Promise<Tip[]> {
    const all: Tip[] = [];
    let page = 1;
    for (;;) {
        const res = await listTips({ page, pageSize: 500 });
        const batch = res.list || [];
        all.push(...batch);
        if (batch.length === 0 || all.length >= res.totalCount) break;
        page += 1;
    }
    return all;
}

/** 某材料挂载的条目 (按材料内顺序). */
export function listMaterialTips(xcode: string): Promise<Tip[]> {
    return request<Tip[]>(`${BASE}/materials/tips/list`, { method: 'POST', dedupe: true, body: { xcode } });
}

/** 某设备挂载的条目 (按设备内顺序, 2026-08-27). */
export function listDeviceTips(deviceKey: string): Promise<Tip[]> {
    return request<Tip[]>(`${BASE}/devices/tips/list`, { method: 'POST', dedupe: true, body: { deviceKey } });
}

// 条目写操作走变更审批: 普通用户的请求判定通过后转工单 (message 含"已提交审批"),
// 调用点用 alertApprovalIfPending 提示且不把本地 UI 当已生效.

export function createTip(payload: SaveTipPayload): Promise<MsgResult<SaveTipResult>> {
    return requestWithMsg<SaveTipResult>(`${BASE}/tips`, { method: 'POST', body: payload });
}

export function updateTip(
    tipCode: string,
    payload: SaveTipPayload,
): Promise<MsgResult<SaveTipResult>> {
    // 同上: 后端从 body 取 tipCode 定位条目, 只发表单字段会当成"条目不存在"
    return requestWithMsg<SaveTipResult>(`${BASE}/tips/update`, {
        method: 'POST',
        body: { tipCode, ...payload },
    });
}

/** 软删除: 条目记录保留, tipCode 不复用, 对平台所有列表/筛选/查重/推荐不可见. */
export function deleteTip(tipCode: string): Promise<MsgResult<null>> {
    return requestWithMsg<null>(`${BASE}/tips/delete`, {
        method: 'POST',
        body: { tipCode },
    });
}

/**
 * 挂载目标: 材料直选 (xcodes) 与分类批量 (cats) 至少给一个, 二者取并集.
 * cats 是一次性展开 —— 后端在落库那一刻把分类解析成该分类下的材料并逐行写挂载关系,
 * 不存"分类→条目"的持久规则, 之后新建的材料不会自动继承.
 */
export interface MountTargets {
    xcodes?: string[];
    /** 分类目标: cat1 = 一级分类编码 (必填), cat2 = 二级分类编码 (空 = 整个一级分类) */
    cats?: { cat1: string; cat2?: string }[];
    /** 设备目标 (2026-08-27): device_key 直选, 与材料/分类目标取并集 */
    deviceKeys?: string[];
}

/** 挂载结果: 两个维度各给 展开总数 / 实际新增 / 跳过 (已挂载). */
export interface MountResult {
    mounted: number;
    resolved: number;
    skipped: number;
    deviceMounted: number;
    deviceResolved: number;
    deviceSkipped: number;
}

/** 挂载条目到一批材料/分类 (一律追加在各材料列表末尾, 已挂载的后端跳过). */
export function mountTip(
    tipCode: string,
    targets: MountTargets,
): Promise<MsgResult<MountResult>> {
    // 业务键走 body (2026-08-24 起后端 mountReq.tipCode 必填), 不能只发 targets
    return requestWithMsg<MountResult>(`${BASE}/tips/mount`, {
        method: 'POST',
        body: { tipCode, ...targets },
    });
}

/** 解除某材料对条目的挂载 (条目仍在库中, 其他材料不受影响). */
export function unmountTip(xcode: string, tipCode: string): Promise<null> {
    return request<null>(`${BASE}/materials/tips/unmount`, {
        method: 'POST',
        body: { xcode, tipCode },
    });
}

/** 解除某设备对条目的挂载 (2026-08-27). */
export function unmountDeviceTip(deviceKey: string, tipCode: string): Promise<null> {
    return request<null>(`${BASE}/devices/tips/unmount`, {
        method: 'POST',
        body: { deviceKey, tipCode },
    });
}

/** 拖拽结束后以当前顺序整表覆盖. */
export function saveTipOrder(xcode: string, tipCodes: string[]): Promise<null> {
    return request<null>(`${BASE}/materials/tips/order`, {
        method: 'POST',
        body: { xcode, tipCodes },
    });
}

/** 设备内顺序落库 (2026-08-27). */
export function saveDeviceTipOrder(deviceKey: string, tipCodes: string[]): Promise<null> {
    return request<null>(`${BASE}/devices/tips/order`, {
        method: 'POST',
        body: { deviceKey, tipCodes },
    });
}

/** 新建材料的通用条目推荐: 同一级分类现有材料在用的 general 条目. */
export function recommendTips(type1: string): Promise<Tip[]> {
    return request<Tip[]>(`${BASE}/tips/recommend`, { query: { type1 } });
}

/** 引用数下钻: 条目已挂载的材料列表. */
export function listTipMaterials(tipCode: string): Promise<TipMaterialRef[]> {
    return request<TipMaterialRef[]>(`${BASE}/tips/materials`, { method: 'POST', dedupe: true, body: { tipCode } });
}

/** 引用数下钻: 条目已挂载的设备列表 (2026-08-27). */
export function listTipDevices(tipCode: string): Promise<TipDeviceRef[]> {
    return request<TipDeviceRef[]>(`${BASE}/tips/devices`, { method: 'POST', dedupe: true, body: { tipCode } });
}

// ---- 批量导入 (xlsx) ------------------------------------------------------

/** 导入模板下载路径 (GET, 直接 <a href> 用; 拼 API_BASE 用 absoluteURL). */
export const TIP_IMPORT_TEMPLATE_PATH = `${BASE}/tips/import-template`;

/** 批量导入结果 (通用 xlsx 导入弹窗的结果形状 + 小贴士特有计数). */
export interface ImportTipsResult {
    total: number;
    valid: number;
    invalid: { row: number; reason: string }[] | null;
    /** 普通用户: 走审批流的工单号; 直接入库时为空 */
    taskCode?: string;
    /** 新建条目数 (走审批时为空) */
    created?: number;
    /** 命中库中已有条目 (标题+补充说明完全相同) 直接引用的条目数 */
    reused?: number;
    /** 挂载目标展开后的材料总数 (去重后) */
    resolved?: number;
    /** 实际新增的挂载关系数 (已挂载的不计) */
    mounted?: number;
}

/**
 * 批量导入小贴士 (multipart xlsx): 一行 = 一个条目 + 它的挂载目标 (分类 / 材料ID, 可留空).
 * 普通用户返回 taskCode 走审批, 本地开发直接入库.
 */
export function importTips(file: File): Promise<ImportTipsResult> {
    return uploadFile<ImportTipsResult>(`${BASE}/tips/import-xlsx`, file);
}

// ---- 挂载维度适配 (材料 / 设备) ------------------------------------------
// 两个维度的接口一一对应, 只差业务键与路径; 页面 (材料详情卡片 / 设备贴士弹窗) 共用
// 同一套组件, 差异全部收在这几个适配函数里 —— 各写一份的话"追加末尾 / 排序 / 分叉提示"
// 这些语义就会两边漂移。

export type TipOwnerKind = 'material' | 'device';

/** 挂载对象上下文 (材料或设备). lights/crafts 给了才收窄表单选项, undefined = 不收窄。 */
export interface TipOwner {
    kind: TipOwnerKind;
    /** 业务键: xcode / deviceKey */
    key: string;
    /** 展示名 */
    name: string;
    lights?: string[];
    crafts?: string[];
}

export function tipOwnerLabel(kind: TipOwnerKind): string {
    return kind === 'device' ? '设备' : '材料';
}

/** 该对象已挂载的条目 (按对象内顺序). */
export function listOwnerTips(o: TipOwner): Promise<Tip[]> {
    return o.kind === 'device' ? listDeviceTips(o.key) : listMaterialTips(o.key);
}

/** 解除该对象对条目的挂载. */
export function unmountOwnerTip(o: TipOwner, tipCode: string): Promise<null> {
    return o.kind === 'device' ? unmountDeviceTip(o.key, tipCode) : unmountTip(o.key, tipCode);
}

/** 对象内顺序落库. */
export function saveOwnerTipOrder(o: TipOwner, tipCodes: string[]): Promise<null> {
    return o.kind === 'device'
        ? saveDeviceTipOrder(o.key, tipCodes)
        : saveTipOrder(o.key, tipCodes);
}

/** 把对象本身作为挂载目标 (勾选添加用). */
export function ownerMountTargets(o: TipOwner): MountTargets {
    return o.kind === 'device' ? { deviceKeys: [o.key] } : { xcodes: [o.key] };
}

/** 条目在该维度上的引用数 (材料侧 refCount / 设备侧 deviceRefCount). */
export function ownerRefCount(t: Tip, kind: TipOwnerKind): number {
    return kind === 'device' ? t.deviceRefCount || 0 : t.refCount || 0;
}

/** 保存条目时带的上下文键 (后端据此判定自动挂载与分叉). */
export function ownerSaveCtx(o: TipOwner): Pick<SaveTipPayload, 'xcode' | 'deviceKey'> {
    return o.kind === 'device' ? { deviceKey: o.key } : { xcode: o.key };
}

/** 条目的总引用数 = 材料 + 设备 (分叉判定按总数, 与后端一致). */
export function totalRefCount(t: Tip): number {
    return (t.refCount || 0) + (t.deviceRefCount || 0);
}

// ---- 枚举字典 ------------------------------------------------------------

export const TIP_TYPES: ReadonlyArray<{ value: TipType; label: string; cls: string }> = [
    { value: 'safety', label: '安全提示', cls: 't-safety' },
    { value: 'special', label: '特殊处理', cls: 't-special' },
    { value: 'general', label: '通用', cls: 't-general' },
];

/** 类型定义文案 (表单内展示, 直接取用). */
export const TIP_TYPE_DESC: Record<TipType, string> = {
    safety: '不做会造成人身伤害、设备损坏或火灾',
    special: '不做会做坏，且只有这个材料需要',
    general: '不做会做坏，一类或所有材料都需要，可批量挂到多个材料',
};

export const LIGHT_ENUM: ReadonlyArray<readonly [string, string]> = [
    ['blue', '蓝光'],
    ['IR', 'IR'],
    ['CO2', 'CO₂'],
    ['UV', 'UV'],
];

export const CRAFT_ENUM: ReadonlyArray<readonly [string, string]> = [
    ['cut', '切割'],
    ['engrave', '雕刻'],
    ['color', '上色'],
    ['emboss', '浮雕'],
];

export const TITLE_MAX = 50;
export const DESC_MAX = 500;

export function enumLabel(list: ReadonlyArray<readonly [string, string]>, v: string): string {
    return list.find(([k]) => k === v)?.[1] || v;
}

/** 适用范围文本: 空维度渲染为「全部光源」/「全部工艺」. */
export function scopeText(t: Pick<Tip, 'light' | 'craft'>): string {
    const l = t.light.length ? t.light.map((v) => enumLabel(LIGHT_ENUM, v)).join('/') : '全部光源';
    const c = t.craft.length ? t.craft.map((v) => enumLabel(CRAFT_ENUM, v)).join('/') : '全部工艺';
    return `${l} · ${c}`;
}

// ---- 共享筛选逻辑 (添加弹窗与小贴士库同一套语义) ---------------------------

export interface TipFilterState {
    type: string;
    light: string;
    craft: string;
    /** 一级分类筛选值 (类别编码 categoryCode); 空 = 不筛 */
    cat1: string;
    /** 设备筛选值 (device_key); 空 = 不筛。派生维度: 条目已挂载在该设备上 */
    deviceKey: string;
}

export const EMPTY_TIP_FILTER: TipFilterState = {
    type: '',
    light: '',
    craft: '',
    cat1: '',
    deviceKey: '',
};

/** 生效的筛选项个数 (「清空筛选 N」的 N). */
export function tipFilterCount(f: TipFilterState): number {
    return [f.type, f.light, f.craft, f.cat1, f.deviceKey].filter(Boolean).length;
}

/**
 * 过滤条目. 语义:
 * - 光源/工艺: 条目对应维度为空数组时命中任何筛选值 (空 = 全部适用)
 * - 一级分类: 派生筛选, 条目已挂载材料的一级分类 (usedCats) 命中所选分类
 *   (cat1Names 传所选分类的 [中文名, 英文键], 兼容后端聚合口径)
 * - 设备: 派生筛选, 条目已挂载设备名 (usedDevices) 命中所选设备
 *   (deviceNames 传所选设备的 [名称, device_key], 与后端 usedDevices 的名称/回退口径对齐)
 * - 搜索: 在 tipCode + title + description 上做不区分大小写的包含匹配
 */
export function filterTips(
    list: Tip[],
    f: TipFilterState,
    cat1Names: string[],
    keyword: string,
    deviceNames: string[] = [],
): Tip[] {
    let out = list;
    if (f.type) out = out.filter((t) => t.type === f.type);
    if (f.light) out = out.filter((t) => t.light.length === 0 || t.light.includes(f.light));
    if (f.craft) out = out.filter((t) => t.craft.length === 0 || t.craft.includes(f.craft));
    if (f.cat1 && cat1Names.length) {
        out = out.filter((t) => (t.usedCats || []).some((c) => cat1Names.includes(c)));
    }
    if (f.deviceKey && deviceNames.length) {
        out = out.filter((t) => (t.usedDevices || []).some((d) => deviceNames.includes(d)));
    }
    const q = keyword.trim().toLowerCase();
    if (q) {
        out = out.filter((t) =>
            (t.tipCode + t.title + t.description).toLowerCase().includes(q),
        );
    }
    return out;
}

/** 同名标记: 有效条目中 title 出现次数 > 1 的标题集合. */
export function dupTitleSet(all: Tip[]): Set<string> {
    const seen = new Map<string, number>();
    for (const t of all) seen.set(t.title, (seen.get(t.title) || 0) + 1);
    const dup = new Set<string>();
    seen.forEach((n, title) => {
        if (n > 1) dup.add(title);
    });
    return dup;
}

/** isImagePath icon 值是否为图片地址 (上传附件的完整 URL; "/" 开头兼容存量相对路径); 否则为 2026-10-16 前的存量 emoji / 图标 key. */
export function isImagePath(v: string): boolean {
    const t = v.trim();
    return t.startsWith('/') || t.startsWith('http://') || t.startsWith('https://');
}
